import { intersection } from 'ramda';
import * as dateUtil from 'js/utils/date.util';
import { getCookie } from 'js/utils/cookie.util';
import { getAffiliateData } from 'js/utils/affiliate.util';
import { TPlayerTag } from 'js/hooks/socket/hooks/useGetPlayerTags/types';
import { TDeviceOS, TDeviceBrowserName } from 'js/hooks/device/store/types';

import * as Types from './types';
import * as constants from './constants';
/**
 * Check if user profile matches one in coma-separated profile list
 */
export const isProfileAllowed = (urlTemplates: string, userUrlTemplates: string[] = []): boolean => {
    if (!urlTemplates || !userUrlTemplates.length) {
        return false;
    }

    const commonUrlTemplates = intersection(
        urlTemplates.split(',').map(template => template.trim()),
        userUrlTemplates.map(template => template.trim())
    );

    return Boolean(commonUrlTemplates.length);
};

export const checkSchedulerItemSettings = ({ schedulerItemSetting, schedulerType, schedulerTimeZone }: {
    schedulerType: string;
    schedulerTimeZone: string;
    schedulerItemSetting: Types.ISchedulerItemSettings;
}): boolean => {
    const { conditionDuration, conditionType } = schedulerItemSetting;
    const isUTCTimezone = schedulerTimeZone === constants.SCHEDULER_CONDITION_TIMEZONES.server;

    switch (schedulerType) {
        case constants.SCHEDULER_TYPES.date: {
            const currentDate = Date.now();
            const startTime = isUTCTimezone
                ? Date.parse(conditionDuration.split(' ')[0])
                : Date.parse(conditionDuration.split(' ')[0].slice(0, -1));

            if (conditionType === constants.SCHEDULER_CONDITION_TYPES.before) {
                return currentDate < startTime;
            }

            if (conditionType === constants.SCHEDULER_CONDITION_TYPES.after) {
                return currentDate >= startTime;
            }

            if (conditionType === constants.SCHEDULER_CONDITION_TYPES.between) {
                const endTime = isUTCTimezone
                    ? Date.parse(conditionDuration.split(' ')[1])
                    : Date.parse(conditionDuration.split(' ')[1].slice(0, -1));

                return startTime <= currentDate && currentDate < endTime;
            }

            return true;
        }
        case constants.SCHEDULER_TYPES.timeOfTheDay: {
            const currentDate = new Date();
            const startTime = conditionDuration.split(' ')[0];
            const endTime = conditionDuration.split(' ')[1];

            if (conditionType === constants.SCHEDULER_CONDITION_TYPES.before) {
                return (
                    `${dateUtil.getFullHours(currentDate, isUTCTimezone)}:${dateUtil.getFullMinutes(
                        currentDate,
                        isUTCTimezone
                    )}` < startTime
                );
            }

            if (conditionType === constants.SCHEDULER_CONDITION_TYPES.after) {
                return (
                    `${dateUtil.getFullHours(currentDate, isUTCTimezone)}:${dateUtil.getFullMinutes(
                        currentDate,
                        isUTCTimezone
                    )}` >= startTime
                );
            }

            if (conditionType === constants.SCHEDULER_CONDITION_TYPES.between) {
                const currentTime = `${dateUtil.getFullHours(currentDate, isUTCTimezone)}:${dateUtil.getFullMinutes(
                    currentDate,
                    isUTCTimezone
                )}`;

                return startTime <= currentTime && currentTime < endTime;
            }

            return true;
        }
        case constants.SCHEDULER_TYPES.dayOfTheWeek: {
            const currentDay = isUTCTimezone ? new Date().getUTCDay() : new Date().getDay();
            const targetDay = Number(conditionDuration);

            return currentDay === targetDay;
        }
        default:
            return true;
    }
};

export const isShownByScheduler = ({ scheduler, schedulerTimeZone }: {
    schedulerTimeZone: string;
    scheduler: Types.ISchedulerItem[][];
}): boolean => {
    if (!scheduler || !scheduler.length) {
        return true;
    }

    return scheduler.some((schedulerGroup) =>
        schedulerGroup.every((schedulerItem) =>
            schedulerItem.schedulerItemSettings.some((schedulerItemSetting) =>
                checkSchedulerItemSettings({
                    schedulerTimeZone,
                    schedulerItemSetting,
                    schedulerType: schedulerItem.schedulerType
                })
            )
        )
    );
};

export const isShownByLogin = ({ isEnabledForGuest, isEnabledForLoggedIn, isLoggedIn }: {
    isLoggedIn: boolean;
    isEnabledForGuest: boolean;
    isEnabledForLoggedIn: boolean;
}): boolean => {
    return isLoggedIn ? isEnabledForLoggedIn : isEnabledForGuest;
};

export const checkURLParameter = ({ conditionName, conditionValue, conditionType, urlSearchParams }: {
    conditionName: string;
    conditionType: string;
    conditionValue: string;
    urlSearchParams: Record<string, string>;
}): boolean => {
    if (!conditionName) {
        return true;
    }

    const params = new URLSearchParams(urlSearchParams);
    const param = params.get(conditionName);

    if (conditionType === constants.SEGMENTATION_CONDITION_TYPES.is) {
        return params.has(conditionName) && conditionValue === (param || '');
    }

    if (conditionType === constants.SEGMENTATION_CONDITION_TYPES.isNot) {
        return params.has(conditionName) && conditionValue !== (param || '');
    }

    if (conditionType === constants.SEGMENTATION_CONDITION_TYPES.exist) {
        return params.has(conditionName);
    }

    if (conditionType === constants.SEGMENTATION_CONDITION_TYPES.notExist) {
        return !params.has(conditionName);
    }

    return true;
};

export const checkURLTemplate = ({
    isLoggedIn,
    conditionType,
    conditionValue,
    segmentationURLTemplates = []
}: {
    isLoggedIn: boolean;
    conditionType: string;
    conditionValue: string;
    segmentationURLTemplates?: string[];
}): boolean => {
    if (!conditionValue || !isLoggedIn) {
        return true;
    }

    if (conditionType === constants.SEGMENTATION_CONDITION_TYPES.is) {
        return segmentationURLTemplates.includes(conditionValue);
    }

    if (conditionType === constants.SEGMENTATION_CONDITION_TYPES.isNot) {
        return !segmentationURLTemplates.includes(conditionValue);
    }

    return true;
};

export const checkCountry = ({ conditionValue, conditionType, userCountry }: {
    userCountry?: string;
    conditionType: string;
    conditionValue: string;
}): boolean => {
    if (!conditionValue || !userCountry) {
        return true;
    }

    if (conditionType === constants.SEGMENTATION_CONDITION_TYPES.is) {
        return conditionValue.toLowerCase() === userCountry.toLowerCase();
    }

    if (conditionType === constants.SEGMENTATION_CONDITION_TYPES.isNot) {
        return conditionValue.toLowerCase() !== userCountry.toLowerCase();
    }

    return true;
};

export const checkBrowser = ({ conditionValue, conditionType, browserName }: {
    conditionType: string;
    conditionValue: string;
    browserName: TDeviceBrowserName;
}): boolean => {
    if (!conditionValue || !browserName) {
        return true;
    }

    if (conditionType === constants.SEGMENTATION_CONDITION_TYPES.is) {
        return conditionValue.toLowerCase() === browserName.toLowerCase();
    }

    if (conditionType === constants.SEGMENTATION_CONDITION_TYPES.isNot) {
        return conditionValue.toLowerCase() !== browserName.toLowerCase();
    }

    return true;
};

export const checkOS = ({ conditionValue, conditionType, os }: {
    os: TDeviceOS;
    conditionType: string;
    conditionValue: string;
}): boolean => {
    if (!conditionValue || !os) {
        return true;
    }

    if (conditionType === constants.SEGMENTATION_CONDITION_TYPES.is) {
        return conditionValue.toLowerCase() === os.toLowerCase();
    }

    if (conditionType === constants.SEGMENTATION_CONDITION_TYPES.isNot) {
        return conditionValue.toLowerCase() !== os.toLowerCase();
    }

    return true;
};

export const checkLanguage = ({ conditionValue, conditionType, userLanguage }: {
    userLanguage?: string;
    conditionType: string;
    conditionValue: string;
}): boolean => {
    if (!conditionValue || !userLanguage) {
        return true;
    }

    if (conditionType === constants.SEGMENTATION_CONDITION_TYPES.is) {
        return conditionValue.toLowerCase() === userLanguage.toLowerCase();
    }

    if (conditionType === constants.SEGMENTATION_CONDITION_TYPES.isNot) {
        return conditionValue.toLowerCase() !== userLanguage.toLowerCase();
    }

    return true;
};

export const checkCookie = ({ cookies, conditionName, conditionValue, conditionType }: {
    cookies: string;
    conditionName: string;
    conditionType: string;
    conditionValue: string;
}): boolean => {
    if (!conditionName) {
        return true;
    }

    const cookie = getCookie(conditionName, cookies);

    if (conditionType === constants.SEGMENTATION_CONDITION_TYPES.is) {
        return cookie !== undefined && conditionValue === (cookie || '');
    }

    if (conditionType === constants.SEGMENTATION_CONDITION_TYPES.isNot) {
        return cookie !== undefined && conditionValue !== (cookie || '');
    }

    if (conditionType === constants.SEGMENTATION_CONDITION_TYPES.exist) {
        return cookie !== undefined;
    }

    if (conditionType === constants.SEGMENTATION_CONDITION_TYPES.notExist) {
        return cookie === undefined;
    }

    return true;
};

export const checkAffiliate = ({ cookies, conditionName, conditionValue, conditionType }: {
    cookies: string;
    conditionName: string;
    conditionType: string;
    conditionValue: string;
}): boolean => {
    if (!conditionName) {
        return true;
    }

    const cookie = getCookie(conditionName, cookies);
    const { advertiser } = getAffiliateData(conditionName, cookies) as { advertiser?: string };

    if (conditionType === constants.SEGMENTATION_CONDITION_TYPES.is) {
        return cookie !== undefined && conditionValue === (advertiser || '');
    }

    if (conditionType === constants.SEGMENTATION_CONDITION_TYPES.isNot) {
        return cookie !== undefined && conditionValue !== (advertiser || '');
    }

    return true;
};

export const checkPlayerTag = ({ conditionName, conditionValue, conditionType, playerTags }: {
    playerTags: TPlayerTag[];
    conditionName: string;
    conditionType: string;
    conditionValue: string;
}): boolean => {
    if (!conditionName) {
        return true;
    }

    const playerTag = playerTags.find((tag) => tag.name === conditionName);

    if (conditionType === constants.SEGMENTATION_CONDITION_TYPES.is) {
        return Boolean(playerTag) && conditionValue === (playerTag?.value || '');
    }

    if (conditionType === constants.SEGMENTATION_CONDITION_TYPES.isNot) {
        return Boolean(playerTag) && conditionValue !== (playerTag?.value || '');
    }

    if (conditionType === constants.SEGMENTATION_CONDITION_TYPES.exist) {
        return Boolean(playerTag);
    }

    if (conditionType === constants.SEGMENTATION_CONDITION_TYPES.notExist) {
        return !playerTag;
    }

    return true;
};

export const checkSegmentationItemSettings = ({ segmentationItemSetting, segmentationType, options }: {
    segmentationType: string;
    options: Types.ISegmentationOptions;
    segmentationItemSetting: Types.ISegmentationItemSettings;
}) => {
    const {
        os,
        cookies,
        isLoggedIn,
        playerTags,
        browserName,
        userCountry,
        userLanguage,
        urlSearchParams,
        segmentationURLTemplates
    } = options;
    const { conditionValue, conditionName, conditionType } = segmentationItemSetting;

    switch (segmentationType) {
        case constants.SEGMENTATION_TYPES.URLParameter: {
            return checkURLParameter({ conditionValue, conditionName, conditionType, urlSearchParams });
        }
        case constants.SEGMENTATION_TYPES.URLTemplate: {
            return checkURLTemplate({ conditionValue, conditionType, segmentationURLTemplates, isLoggedIn });
        }
        case constants.SEGMENTATION_TYPES.country: {
            return checkCountry({ conditionValue, conditionType, userCountry });
        }
        case constants.SEGMENTATION_TYPES.browser: {
            return checkBrowser({ conditionValue, conditionType, browserName });
        }
        case constants.SEGMENTATION_TYPES.os: {
            return checkOS({ conditionValue, conditionType, os });
        }
        case constants.SEGMENTATION_TYPES.language: {
            return checkLanguage({ conditionValue, conditionType, userLanguage });
        }
        case constants.SEGMENTATION_TYPES.cookie: {
            return checkCookie({ cookies, conditionValue, conditionName, conditionType });
        }
        case constants.SEGMENTATION_TYPES.affiliate: {
            return checkAffiliate({ cookies, conditionValue, conditionName, conditionType });
        }
        case constants.SEGMENTATION_TYPES.playerTag: {
            return checkPlayerTag({ conditionValue, conditionName, conditionType, playerTags });
        }
        default:
            return true;
    }
};

export const isShownBySegmentation = ({ loggedInSegmentation, guestSegmentation, options }: {
    options: Types.ISegmentationOptions;
    loggedInSegmentation: Types.ISegmentationItem[][];
    guestSegmentation: Types.ISegmentationItem[][];
}): boolean => {
    const segmentationSettings = options.isLoggedIn ? loggedInSegmentation : guestSegmentation;

    if (!segmentationSettings || !segmentationSettings.length) {
        return true;
    }

    return segmentationSettings.some((segmentationGroup) =>
        segmentationGroup.every((segmentationItem) =>
            segmentationItem.segmentationItemSettings.some((segmentationItemSetting) =>
                checkSegmentationItemSettings({
                    options,
                    segmentationItemSetting,
                    segmentationType: segmentationItem.segmentationType
                })
            )
        )
    );
};

export const getSegmentationPlayerTags = (segmentationSettings: Types.ISegmentationItem[][]): string[] => {
    const playerTags: string[] = [];

    if (!segmentationSettings.length) {
        return [];
    }

    segmentationSettings.forEach((segmentationGroup) =>
        segmentationGroup.forEach((segmentationItem) =>
            segmentationItem.segmentationItemSettings.forEach(({ conditionName }) => {
                if (segmentationItem.segmentationType === constants.SEGMENTATION_TYPES.playerTag) {
                    if (!conditionName || playerTags.includes(conditionName)) {
                        return;
                    }

                    playerTags.push(conditionName);
                }
            })
        )
    );

    return playerTags;
};
